import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import { CheckSquare, Mail, Search, Send, Square, UserCheck, Users } from "lucide-react";
import { getReport } from "../../../api";
import ExportToExcel from "../../../components/ExportToExcel/ExportToExcel";
import PageHeader from "../../../components/common/PageHeader/PageHeader";
import TablesV2 from "../../../components/common/Tables/TablesV2";
import { useAuth } from "../../../context/AuthContext";
import { useNightMode } from "../../../contexts/NightModeContext";
import Background from "../../Login/Background";

const EventContactList = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const { isNightMode } = useNightMode();

  const [contact, setContactList] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const getContactList = async () => {
    try {
      setIsLoading(true);
      if (!user) return;

      const eventData = localStorage.getItem("eventData");
      if (!eventData) return;

      const eventId = JSON.parse(eventData).eventId;
      const response = await getReport(eventId);

      if (response) {
        const seenEmails = new Set<string>();
        const updatedResponse = response
          .filter((item: any) => item.bookingStatus === "confirmed")
          .map((item: any) => {
            const primaryContact = item.bookingObject?.primaryContactDetails;
            if (!primaryContact) return null;

            const emailKey = primaryContact.email?.toLowerCase();
            if (emailKey && seenEmails.has(emailKey)) return null;
            if (emailKey) seenEmails.add(emailKey);

            return {
              contactId: item.contactId || item.bookingId,
              firstName: primaryContact.firstName || "",
              lastName: primaryContact.lastName || "",
              email: primaryContact.email || "",
              phoneNo: primaryContact.phoneNo || "",
              address: primaryContact.address || "",
              city: primaryContact.city || "",
              state: primaryContact.state || "",
              zipCode: primaryContact.zipCode || "",
              country: primaryContact.country || "",
            };
          })
          .filter(Boolean);

        setContactList(updatedResponse);
        setRowSelection({});
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getContactList();
  }, [slug, user]);

  const getInitials = (firstName?: string, lastName?: string) => {
    const f = firstName ? firstName.charAt(0).toUpperCase() : "";
    const l = lastName ? lastName.charAt(0).toUpperCase() : "";
    return `${f}${l}` || "EC";
  };

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <input
            type="checkbox"
            className="form-check-input cursor-pointer"
            checked={table.getIsAllRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            className="form-check-input cursor-pointer"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "fullName",
        header: "FULL NAME",
        accessorFn: (row) => `${row.firstName || ""} ${row.lastName || ""}`.trim() || row.email || row.phoneNo || "Guest Contact",
        cell: ({ row }) => {
          const item = row.original;
          const fullName = `${item.firstName || ""} ${item.lastName || ""}`.trim() || item.email || item.phoneNo || "Guest Contact";
          return (
            <div className="d-flex align-items-center gap-3">
              <div className="contact-avatar">{getInitials(item.firstName, item.lastName)}</div>
              <div className="fw-bold contact-name-text fs-13px">{fullName}</div>
            </div>
          );
        },
      },
      {
        accessorKey: "email",
        header: "EMAIL ADDRESS",
        cell: ({ row }) => <span className="font-mono contact-sub-text fs-12px">{row.original.email || "N/A"}</span>,
      },
      {
        accessorKey: "phoneNo",
        header: "PHONE NO",
        cell: ({ row }) => <span className="font-mono contact-sub-text fs-12px">{row.original.phoneNo || "N/A"}</span>,
      },
      {
        id: "location",
        header: "LOCATION",
        accessorFn: (row) => [row.address, row.city, row.state, row.country].filter(Boolean).join(", ") || "N/A",
        cell: ({ row }) => {
          const item = row.original;
          const location = [item.address, item.city, item.state, item.country].filter(Boolean).join(", ");
          return <span className="contact-sub-text fs-12px">{location || "N/A"}</span>;
        },
      },
      {
        accessorKey: "zipCode",
        header: "ZIP CODE",
        cell: ({ row }) => <span className="font-mono contact-sub-text fs-12px">{row.original.zipCode || "N/A"}</span>,
      },
    ],
    []
  );

  const selectedCount = Object.keys(rowSelection).filter((key) => rowSelection[key]).length;
  const isAllSelected = contact.length > 0 && selectedCount === contact.length;

  const handleToggleSelectAll = () => {
    if (isAllSelected) {
      setRowSelection({});
      return;
    }

    const newSelection: RowSelectionState = {};
    contact.forEach((item, idx) => {
      const id = item.contactId || item.id || `ec-${idx}`;
      newSelection[id] = true;
    });
    setRowSelection(newSelection);
  };

  const sendEmailToSelected = () => {
    const selectedIds = Object.keys(rowSelection).filter((key) => rowSelection[key]);
    const selectedEmails = contact
      .filter((item, idx) => selectedIds.includes(item.contactId || item.id || `ec-${idx}`))
      .map((item) => item.email)
      .filter(Boolean);

    if (selectedEmails.length === 0) {
      alert("No valid email addresses found.");
      return;
    }

    window.location.href = `mailto:?bcc=${selectedEmails.join(",")}&subject=From Eventox&body=Check Out Eventox,`;
  };

  const contactsWithEmailCount = useMemo(() => contact.filter((item) => Boolean(item.email)).length, [contact]);

  return (
    <div className={`contact-list-container ${isNightMode ? "text-white" : "text-dark"}`}>
      <Background />

      <PageHeader
        title="Event Contact List"
        subtitle="Review confirmed booking contacts, manage selections, and export event registry sheets."
        icon={Users}
        isLoading={isLoading}
        actions={<ExportToExcel data={contact} fileName="event_contact_list" buttonText="Export Contact Sheet (.CSV)" />}
      />

      <div className="contact-stats-grid">
        <div className="contact-stat-card">
          <div>
            <div className="contact-stat-label">TOTAL CONTACTS</div>
            <div className="contact-stat-value">{isLoading ? "..." : contact.length} <span className="fs-14px fw-normal contact-sub-text">contacts</span></div>
          </div>
          <div className="contact-stat-icon pink"><Users size={22} /></div>
        </div>

        <div className="contact-stat-card">
          <div>
            <div className="contact-stat-label">SELECTED CONTACTS</div>
            <div className="contact-stat-value">{selectedCount} <span className="fs-14px fw-normal contact-sub-text">selected</span></div>
          </div>
          <div className="contact-stat-icon orange"><UserCheck size={22} /></div>
        </div>

        <div className="contact-stat-card">
          <div>
            <div className="contact-stat-label">EMAIL DESTINATIONS</div>
            <div className="contact-stat-value">{isLoading ? "..." : contactsWithEmailCount} <span className="fs-14px fw-normal contact-sub-text">valid emails</span></div>
          </div>
          <div className="contact-stat-icon green"><Mail size={22} /></div>
        </div>
      </div>

      <div className="contact-filter-card">
        <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
          <div className="position-relative flex-grow-1 w-100">
            <Search className="position-absolute start-0 top-50 translate-middle-y ms-3 contact-sub-text" size={18} />
            <input
              type="text"
              className="contact-search-input"
              placeholder="Filter by name, email, phone, city or country..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="d-flex align-items-center gap-2 flex-shrink-0">
            <button type="button" className="contact-action-btn" onClick={handleToggleSelectAll} disabled={isLoading || contact.length === 0}>
              {isAllSelected ? <><CheckSquare size={16} className="text-pink" /> Unselect All</> : <><Square size={16} /> Select All</>}
            </button>

            {selectedCount > 0 && (
              <button type="button" className="contact-action-btn primary" onClick={sendEmailToSelected}>
                <Send size={15} /> Send Email ({selectedCount})
              </button>
            )}
          </div>
        </div>
      </div>

      <TablesV2
        data={contact}
        columns={columns}
        isLoading={isLoading}
        searchQuery={searchQuery}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        getRowId={(row, idx) => row.contactId || row.id || `ec-${idx}`}
        loadingMessage="Loading Event Contact Registry..."
        emptyMessage="No event contacts found matching your current search criteria."
      />
    </div>
  );
};

export default EventContactList;